import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './NotificationToast.css';

/**
 * NotificationToast Component
 * 
 * Displays push notifications as toast messages in the bottom-right corner.
 * Handles auto-dismiss, click actions, and provides a clean notification UI.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the toast
 * @param {Function} props.onClose - Callback when toast is closed
 * @param {Function} props.onClick - Callback when toast is clicked
 * @param {Object} props.notification - Notification data
 * @param {string} props.notification.title - Notification title
 * @param {string} props.notification.body - Notification body
 * @param {string} props.notification.icon - Notification icon URL
 * @param {string} props.notification.url - Notification click URL
 * @param {number} props.autoHideDuration - Auto-hide duration in ms (default: 6000)
 * @returns {JSX.Element|null} NotificationToast component or null if not shown
 */
const NotificationToast = ({
  show,
  onClose,
  onClick,
  notification = {},
  autoHideDuration = 6000
}) => {
  const { t } = useTranslation();

  // Auto-hide timer
  useEffect(() => {
    if (show && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        onClose && onClose();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [show, autoHideDuration, onClose]);
  
  if (!show) {
    return null;
  }

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (notification.url) {
      window.open(notification.url, '_blank');
    }
    onClose && onClose();
  };

  const handleClose = (e) => {
    e.stopPropagation();
    onClose && onClose();
  };

  return (
    <div className={`notification-toast ${show ? 'show' : ''}`}>
      <div 
        className="notification-toast-content"
        onClick={handleClick}
        style={{ cursor: notification.url || onClick ? 'pointer' : 'default' }}
      >
        {/* Header */}
        <div className="notification-toast-header">
          {notification.icon && (
            <img
              src={notification.icon}
              alt=""
              className="notification-toast-icon"
            />
          )}
          <div className="notification-toast-title">
            {notification.title || t('notification')}
          </div>
          <button
            className="notification-toast-close"
            onClick={handleClose}
            aria-label={t('close')}
          >
            ×
          </button>
        </div>

        {/* Body */}
        {notification.body && (
          <div className="notification-toast-body">
            {notification.body}
          </div>
        )}

        {/* Progress bar for auto-hide */}
        {autoHideDuration > 0 && (
          <div 
            className="notification-toast-progress"
            style={{ 
              animationDuration: `${autoHideDuration}ms` 
            }}
          />
        )}
      </div>
    </div>
  );
};

export default NotificationToast; 